/**
 * Connectivity Diagnostic Utility
 *
 * Runs lightweight checks to diagnose WHY a user can't connect.
 * Used on login failures to provide actionable error messages
 * and capture diagnostic data for Sentry/support.
 */

import { config } from '@/config';
import { extractErrorMessage } from './error';

export interface ConnectivityReport {
  /** Browser reports online */
  browserOnline: boolean;
  /** /health/live endpoint reachable */
  apiReachable: boolean | null;
  /** API response time in ms (null if unreachable) */
  apiLatencyMs: number | null;
  /** Specific failure reason if API unreachable */
  apiError: string | null;
  /** Whether cookies are enabled in the browser */
  cookiesEnabled: boolean;
  /** Whether the CSRF cookie exists */
  hasCsrfCookie: boolean;
  /** User agent string for browser identification */
  userAgent: string;
  /** Timestamp of the diagnostic */
  timestamp: string;
  /** API base URL being used */
  apiBaseUrl: string;
}

/**
 * Run a quick connectivity diagnostic.
 * Designed to be fast (2s timeout) and non-blocking.
 */
export async function runConnectivityDiagnostic(): Promise<ConnectivityReport> {
  const report: ConnectivityReport = {
    browserOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
    apiReachable: null,
    apiLatencyMs: null,
    apiError: null,
    cookiesEnabled: typeof navigator !== 'undefined' ? navigator.cookieEnabled : true,
    hasCsrfCookie: typeof document !== 'undefined' ? /(?:^|; )csrf-token=/.test(document.cookie) : false,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
    timestamp: new Date().toISOString(),
    apiBaseUrl: config.api.baseUrl,
  };

  // Quick health check with 4s timeout
  if (report.browserOnline) {
    const start = performance.now();
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 4000);
      const response = await fetch(`${config.api.baseUrl.replace(/\/api$/, '')}/health/live`, {
        method: 'GET',
        signal: controller.signal,
        // Don't send credentials for health check — avoids CORS preflight issues
        credentials: 'omit',
        cache: 'no-store',
      }).finally(() => clearTimeout(timeoutId));

      report.apiLatencyMs = Math.round(performance.now() - start);
      report.apiReachable = response.ok;
      if (!response.ok) {
        report.apiError = `HTTP ${response.status}`;
      }
    } catch (err) {
      report.apiLatencyMs = Math.round(performance.now() - start);
      report.apiReachable = false;
      if (err instanceof DOMException && err.name === 'AbortError') {
        report.apiError = 'timeout';
      } else if (err instanceof TypeError) {
        // TypeError from fetch = network-level failure (DNS, CORS, firewall, etc.)
        report.apiError = err.message;
      } else {
        report.apiError = extractErrorMessage(err, 'Unknown network error');
      }
    }
  }

  return report;
}

/**
 * Generate a user-friendly troubleshooting message based on diagnostic results.
 */
export function getTroubleshootingMessage(report: ConnectivityReport): string {
  if (!report.browserOnline) {
    return 'Your device appears to be offline. Please check your internet connection and try again.';
  }

  if (report.apiReachable === false) {
    if (report.apiError === 'timeout') {
      return 'Our server is taking too long to respond. This may be caused by a slow network connection, VPN, or firewall. Please try again, or try from a different network.';
    }
    if (report.apiError?.includes('Failed to fetch') || report.apiError?.includes('NetworkError')) {
      return 'Your browser cannot reach our servers. This is commonly caused by a firewall, VPN, ad blocker, or browser extension. Try disabling extensions or connecting from a different network.';
    }
    return 'We cannot reach our servers from your network. Please try: (1) disabling your VPN or ad blocker, (2) trying in an incognito/private window, or (3) connecting from a different network.';
  }

  if (!report.cookiesEnabled) {
    return 'Your browser has cookies disabled. Voley requires cookies to keep you signed in. Please enable cookies in your browser settings.';
  }

  // API is reachable but login still failed — could be cookie/CORS issue
  return 'Our servers are reachable but login failed. This may be caused by a browser extension blocking cookies. Please try in an incognito/private window.';
}

/**
 * Format the diagnostic report for console/support output.
 */
export function formatDiagnosticForSupport(report: ConnectivityReport): string {
  return [
    `--- Voley Connection Diagnostic ---`,
    `Time: ${report.timestamp}`,
    `Browser Online: ${report.browserOnline}`,
    `API Reachable: ${report.apiReachable}`,
    `API Latency: ${report.apiLatencyMs !== null ? `${report.apiLatencyMs}ms` : 'N/A'}`,
    `API Error: ${report.apiError || 'none'}`,
    `Cookies Enabled: ${report.cookiesEnabled}`,
    `CSRF Cookie: ${report.hasCsrfCookie}`,
    `API URL: ${report.apiBaseUrl}`,
    `Browser: ${report.userAgent}`,
    `-----------------------------------`,
  ].join('\n');
}
